import { Alert } from '@mui/material';
import { toNumber } from 'lodash';
import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LayoutBuilderWithDisclaimer } from 'src/components/layoutBuilder';
import { calculateIsGridFull } from 'src/components/layoutBuilder/LayoutBuilder.helper';
import { Progress } from 'src/components/progress/Progress';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useCreateScreenSection } from 'src/hooks/api/layouts/useCreateScreenSection';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { ISidebarStates } from 'src/interfaces';
import { isCurrentScreenWithDYR, lang, useUserRole } from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import { LayoutsEditPageHeader } from '../editLayoutsPage/modules/LayoutsEditPageHeader/LayoutsEditPageHeader';
import {
  createCODPayload,
  createDisclaimerPayload,
  createSectionPayload,
} from './CreateSectionsLayoutsPage.helper';
import {
  CreateSectionsLayoutsContainer,
  CreateSectionsLayoutsContentContainer,
} from './CreateSectionsLayoutsPage.styled';
import { useCreateLayoutsPage } from './hooks/useCreateLayoutsPage.hook';

export const CreateSectionsLayoutsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [showLeaveModal, setShowLeaveModal] = useState(true);

  const { isViewOnly } = useUserRole();
  const { layoutId = '' } = useParams();

  useNavigateBlocker(showLeaveModal);

  const screenIndex = queryParams.get('screen') ?? '0';

  const {
    layouts,
    screenName,
    isLayoutScreensLoading,
    gridHeight,
    screenWidth,
    rows,
    cols,
    gridSectionHeight,
    gridSectionWidth,
    setGridSections,
    disclaimerPosition,
    disclaimerType,
    disclaimerWidth,
    disclaimerWidthOriginal,
    disclaimerHeightScaled,
    extraHeightForDisclaimerTopAdjustment,
    gridSections,
    disclaimerHeightOriginal,
    globalComponentsScreen,
  } = useCreateLayoutsPage(toNumber(layoutId), toNumber(screenIndex));
  const createScreenSection = useCreateScreenSection();

  const isGridFull = calculateIsGridFull(gridSections ?? [], cols, rows);
  const { hasDYR } = checkSectionTypes(layouts?.[0]?.config?.sectionTypes);
  const navigateBack = useCallback(
    () =>
      navigate(PATHS.ASSEMBLY_LAYOUTS_EDIT(layoutId), {
        state: { sidebar: ISidebarStates.DEFAULT },
      }),
    [layoutId, navigate],
  );

  const handleSaveScreen = async () => {
    const sectionPayload = createSectionPayload(
      gridSections,
      gridHeight,
      screenWidth,
      rows,
      cols,
      extraHeightForDisclaimerTopAdjustment,
    );
    const disclaimerPayload = createDisclaimerPayload(
      disclaimerType,
      disclaimerPosition,
      disclaimerHeightOriginal,
      disclaimerWidthOriginal,
      gridHeight,
    );
    const CODPayload = createCODPayload(
      layouts?.[0]?.config?.sectionTypes || [],
      screenIndex,
      disclaimerHeightOriginal,
      disclaimerPosition,
      layouts?.[0]?.cod_position,
      globalComponentsScreen,
    );

    await createScreenSection.mutateAsync({
      sections: [...disclaimerPayload, ...sectionPayload, ...CODPayload],
      screen: layouts?.[0]?.screens?.[Number(screenIndex) - 1],
      layoutId,
      layoutGridLine:
        layouts?.[0]?.config?.gridLines?.[Number(screenIndex) - 1] ?? '',
      sectionTypes: layouts?.[0]?.config?.sectionTypes,
      codPosition: layouts?.[0]?.cod_position,
      globalComponentsScreen,
    });

    // ensure that showLeaveModal is updated before the navigation attempt.
    flushSync(() => {
      setShowLeaveModal(false);
    });

    Snackbar.success(lang.editLayoutsSidebar.sectionCreateSuccess(screenName));

    navigateBack();
  };

  if (isLayoutScreensLoading) {
    return <Progress />;
  }

  return (
    <CreateSectionsLayoutsContainer>
      <CreateSectionsLayoutsContentContainer>
        <LayoutsEditPageHeader
          layoutName={layouts?.[0]?.name}
          screenName={screenName}
          navigateBack={navigateBack}
          isViewOnly={isViewOnly}
          isLoading={createScreenSection.isLoading}
          handleSaveScreen={handleSaveScreen}
          isSaveDisabled={!isGridFull || createScreenSection.isLoading}
        />
        <div>
          <Alert
            severity="info"
            icon={false}
            sx={{ width: 'fit-content', margin: 'auto', marginBottom: '20px' }}>
            {lang.editLayoutsSidebar.createSectionAlert}
          </Alert>
          <LayoutBuilderWithDisclaimer
            gridSections={gridSections}
            rows={rows}
            cols={cols}
            cellHeight={gridSectionHeight}
            cellWidth={gridSectionWidth}
            setGridSections={setGridSections}
            isRecommendationEnabled={
              hasDYR &&
              isCurrentScreenWithDYR(
                Number(screenIndex),
                globalComponentsScreen,
              )
            }
            disclaimerPosition={disclaimerPosition}
            disclaimerType={disclaimerType}
            disclaimerHeightScaled={disclaimerHeightScaled}
            disclaimerWidth={disclaimerWidth}
          />
        </div>
      </CreateSectionsLayoutsContentContainer>
    </CreateSectionsLayoutsContainer>
  );
};
